import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, Phone, Mail, Award, Network, X, GripVertical, ArrowUpToLine, Download, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAwayMap, awayFor, AwayBadge, AwayNote } from "@/components/AwayBadge";

function buildTree(staff) {
  const byName = {};
  staff.forEach((s) => {  // same-name duplicates (e.g. two O365 accounts) collapse into one node, non-empty fields win
    const filled = Object.fromEntries(Object.entries(s).filter(([, v]) => v !== "" && v != null));
    byName[s.name] = { ...(byName[s.name] || {}), ...filled, children: [] };
  });
  const roots = [];
  Object.values(byName).forEach((node) => {
    if (node.reports_to && byName[node.reports_to] && node.reports_to !== node.name) byName[node.reports_to].children.push(node);
    else roots.push(node);
  });
  const size = (n) => 1 + n.children.reduce((a, k) => a + size(k), 0);
  const bySize = (a, b) => (b._n - a._n) || (a.name || "").localeCompare(b.name || "");
  const annotate = (n) => { n._n = size(n); n.children.forEach(annotate); };
  const sortRec = (n) => { n.children.sort(bySize); n.children.forEach(sortRec); };
  roots.forEach(annotate);
  roots.forEach(sortRec);
  roots.sort(bySize);
  return roots;
}

function flatten(roots, expanded) {
  const rows = [];
  const walk = (node, depth) => {
    const hasKids = node.children.length > 0;
    rows.push({ node, depth, hasKids });
    if (hasKids && expanded[node.id]) node.children.forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));
  return rows;
}

function initials(n) { return (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join(""); }

function Avatar({ src, name, cls }) {
  const [err, setErr] = useState(false);
  if (!src || err) return <span className={`grid place-items-center ${cls} rounded-sm bg-primary text-primary-foreground font-display font-bold shrink-0`}>{initials(name)}</span>;
  return <img src={src} alt={name} onError={() => setErr(true)} className={`${cls} rounded-sm object-cover pointer-events-none shrink-0`} />;
}

// is `descName` a descendant of `ancestorName` in the current staff list?
function isDescendant(staff, ancestorName, descName) {
  let cur = staff.find((s) => s.name === descName);
  const seen = new Set();
  while (cur && cur.reports_to && !seen.has(cur.name)) {
    if (cur.reports_to === ancestorName) return true;
    seen.add(cur.name);
    cur = staff.find((s) => s.name === cur.reports_to);
  }
  return false;
}

export default function OrgChart() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [staff, setStaff] = useState([]);
  const [sel, setSel] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [query, setQuery] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const awayMap = useAwayMap();

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const roots = useMemo(() => buildTree(staff), [staff]);

  useEffect(() => {
    const byId = {};
    buildTree(staff).forEach(function rec(n) { byId[n.id] = true; n.children.forEach(rec); });
    setExpanded(byId);
  }, [staff]);

  const rows = flatten(roots, expanded);
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return staff.filter((s) => (s.name || "").toLowerCase().includes(term) || (s.title || "").toLowerCase().includes(term)).slice(0, 8);
  }, [query, staff]);

  const jumpTo = (person) => {
    // expand every ancestor so the person becomes visible
    const anc = {};
    const seen = new Set();
    let cur = staff.find((s) => s.name === person.reports_to);
    while (cur && !seen.has(cur.name)) {
      anc[cur.id] = true;
      seen.add(cur.name);
      cur = staff.find((s) => s.name === cur.reports_to);
    }
    anc[person.id] = true;
    setExpanded((e) => ({ ...e, ...anc }));
    setSel(person);
    setHighlightId(person.id);
    setQuery("");
    setTimeout(() => {
      const el = document.querySelector(`[data-node-id="${person.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 140);
    setTimeout(() => setHighlightId(null), 2800);
  };

  const reassign = async (draggedId, newManagerName) => {
    const dragged = staff.find((s) => s.id === draggedId);
    if (!dragged) return;
    if (dragged.name === newManagerName) return;
    if (newManagerName && isDescendant(staff, dragged.name, newManagerName)) {
      toast.error("Can't move a manager under one of their own reports.");
      return;
    }
    // optimistic update
    setStaff((prev) => prev.map((s) => (s.id === draggedId ? { ...s, reports_to: newManagerName } : s)));
    try {
      await api.put(`/staff/${draggedId}`, { reports_to: newManagerName });
      toast.success(newManagerName ? `${dragged.name} now reports to ${newManagerName}` : `${dragged.name} moved to top level`);
    } catch { toast.error("Could not update reporting line"); load(); }
  };

  const onDrop = (targetNode) => {
    if (!isAdmin || !dragId) return;
    reassign(dragId, targetNode ? targetNode.name : "");
    setDragId(null); setDropTarget(null);
  };

  const handleImport = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/org-chart/import", fd);
      setImportResult(data);
      setImportOpen(true);
      toast.success(`Updated ${data.matched} reporting line${data.matched === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="orgchart-page">
      <PageHeader overline="People & HR" title="Interactive Org Chart" subtitle="Who reports to whom — expand branches and click anyone to see details." />

      {isAdmin && (
        <div className="flex justify-end gap-2 mb-4">
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="org-import-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImport(f); e.target.value = ""; }} />
          <Button variant="outline" size="sm" data-testid="org-import-btn" disabled={importing} onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1.5" /> {importing ? "Importing…" : "Import HR File"}
          </Button>
          <Button variant="outline" size="sm" data-testid="org-export-pdf" onClick={() => window.open(`${BACKEND}/api/org-chart/pdf`, "_blank")}>
            <Download className="h-4 w-4 mr-1.5" /> Export PDF
          </Button>
        </div>
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md" data-testid="org-import-result">
          <DialogHeader>
            <DialogTitle>Org chart updated</DialogTitle>
            <DialogDescription>Reporting lines were refreshed from your HR headcount file.</DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <div className="flex-1 border border-border rounded-sm p-3 text-center"><div className="text-2xl font-condensed font-extrabold">{importResult.people}</div><div className="text-[11px] uppercase text-muted-foreground">In file</div></div>
                <div className="flex-1 border border-border rounded-sm p-3 text-center"><div className="text-2xl font-condensed font-extrabold text-emerald-600">{importResult.matched}</div><div className="text-[11px] uppercase text-muted-foreground">Lines updated</div></div>
              </div>
              {importResult.unmatched_persons?.length > 0 && (
                <div><div className="text-xs font-semibold mb-1">Not in the directory ({importResult.unmatched_persons.length})</div>
                  <div className="text-xs text-muted-foreground max-h-28 overflow-y-auto">{importResult.unmatched_persons.join(", ")}</div></div>
              )}
              {importResult.unmatched_supervisors?.length > 0 && (
                <div><div className="text-xs font-semibold mb-1">Unmatched supervisors ({importResult.unmatched_supervisors.length})</div>
                  <div className="text-xs text-muted-foreground max-h-28 overflow-y-auto">{importResult.unmatched_supervisors.join(", ")}</div></div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => setImportOpen(false)} data-testid="org-import-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground bg-secondary/60 border border-border rounded-sm px-3 py-2" data-testid="orgchart-dnd-hint">
          <GripVertical className="h-3.5 w-3.5 text-primary" /> Drag any person onto another to change who they report to. Drop on the top bar to make them top-level.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 animate-fade-up">
          <div className="relative mb-3" data-testid="org-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              data-testid="org-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a person to jump to them…"
              className="w-full pl-9 pr-9 py-2 text-sm bg-card border border-border rounded-sm outline-none focus:border-primary transition-colors"
            />
            {query && (
              <button data-testid="org-search-clear" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
            {matches.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid="org-search-results">
                {matches.map((m) => (
                  <button key={m.id} data-testid="org-search-result" onClick={() => jumpTo(m)}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-accent transition-colors">
                    <Avatar src={m.photo_url} name={m.name} cls="h-7 w-7 text-[10px]" />
                    <span className="min-w-0">
                      <span className="block text-sm truncate">{m.name}</span>
                      <span className="block text-xs text-muted-foreground truncate">{m.title}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {isAdmin && (
            <div
              data-testid="org-top-drop"
              onDragOver={(e) => { e.preventDefault(); setDropTarget("__top__"); }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={() => onDrop(null)}
              className={`mb-2 flex items-center justify-center gap-2 border-2 border-dashed rounded-sm py-2 text-xs transition-colors ${dropTarget === "__top__" ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}
            >
              <ArrowUpToLine className="h-3.5 w-3.5" /> Drop here to make top-level (no manager)
            </div>
          )}

          {rows.map(({ node, depth, hasKids }) => {
            const isSel = sel && sel.id === node.id;
            const isDrop = dropTarget === node.id;
            return (
              <div
                key={node.id}
                data-node-id={node.id}
                data-testid="org-node"
                draggable={isAdmin}
                onClick={() => setSel(node)}
                onDragStart={(e) => { setDragId(node.id); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                onDragOver={(e) => { if (isAdmin && dragId && dragId !== node.id) { e.preventDefault(); setDropTarget(node.id); } }}
                onDragLeave={() => setDropTarget((t) => (t === node.id ? null : t))}
                onDrop={(e) => { e.preventDefault(); onDrop(node); }}
                className={`flex items-center gap-3 border rounded-sm px-3 py-2.5 mb-2 cursor-pointer transition-colors ${node.id === highlightId ? "ring-2 ring-primary ring-offset-1 border-primary" : isDrop ? "border-primary bg-primary/10 ring-1 ring-primary" : isSel ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/60"}`}
                style={{ marginLeft: depth * 28 }}
              >
                {isAdmin && <GripVertical className="h-4 w-4 text-muted-foreground/50 shrink-0 cursor-grab" />}
                {hasKids ? (
                  <button onClick={(e) => { e.stopPropagation(); toggle(node.id); }} data-testid="org-toggle" className="text-muted-foreground hover:text-primary">
                    {expanded[node.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                ) : <span className="w-4" />}
                <Avatar src={node.photo_url} name={node.name} cls="h-9 w-9 text-xs" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold leading-tight truncate">{node.name}</div>
                  <div className="text-xs text-primary truncate">{node.title}</div>
                </div>
                <AwayBadge away={awayFor(awayMap, node.email)} compact className="ml-1 shrink-0" />
                {hasKids && <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">{node.children.length}</span>}
              </div>
            );
          })}
        </div>

        <div>
          <div className="border border-border bg-card rounded-sm p-6 sticky top-2" data-testid="org-detail">
            {!sel ? (
              <div className="text-center text-muted-foreground py-10">
                <Network className="h-8 w-8 mx-auto mb-3 opacity-60" />
                <p className="text-sm">Select someone from the chart to view details.</p>
              </div>
            ) : (
              <div>
                <button onClick={() => setSel(null)} className="float-right text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                <div className="mb-4"><Avatar src={sel.photo_url} name={sel.name} cls="h-20 w-20 text-xl" /></div>
                <h3 className="font-display font-bold text-lg">{sel.name}</h3>
                <div className="text-sm text-primary">{sel.title}</div>
                <div className="text-xs text-muted-foreground mb-4">{sel.practice_group}</div>
                <AwayNote away={awayFor(awayMap, sel.email)} />
                <p className="text-sm text-muted-foreground border-t border-border pt-3 mt-3">{sel.bio}</p>
                <div className="mt-4 space-y-2 text-sm">
                  {sel.bar_admissions && sel.bar_admissions !== "—" && <div className="flex items-center gap-2 text-muted-foreground"><Award className="h-3.5 w-3.5" /> {sel.bar_admissions}</div>}
                  {sel.extension && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> Ext. <span className="font-condensed font-semibold">{sel.extension}</span></div>}
                  {sel.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /> {sel.email}</div>}
                </div>
                {sel.reports_to && <div className="mt-4 text-xs bg-secondary/60 border border-border p-2.5 rounded-sm">Reports to <span className="font-semibold text-foreground">{sel.reports_to}</span></div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
